import { motion } from "framer-motion";
import { MapPin, Phone, Instagram, Clock, MessageCircle, Navigation } from "lucide-react";
import { SITE, WHATSAPP_URL, MAPS_URL, MAPS_EMBED } from "@/constants/site";

export default function Location() {
  return (
    <section
      id="localizacao"
      data-testid="location-section"
      className="relative py-24 md:py-32 bg-[#0A0A0A] overflow-hidden"
    >
      <div className="absolute inset-0 bg-noise opacity-[0.025]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="mb-14">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-xs uppercase tracking-[0.35em] text-red-500 font-bold mb-6 flex items-center gap-3"
          >
            <span className="w-8 h-px bg-red-500" /> Onde Estamos
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-display text-4xl md:text-5xl lg:text-6xl font-black uppercase leading-[1] tracking-tighter text-white"
          >
            Venha nos <span className="text-red-600">visitar.</span>
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            id="contato"
            className="lg:col-span-5 space-y-4"
          >
            {/* Address */}
            <div className="bg-zinc-900/40 backdrop-blur-md border border-zinc-800/80 p-6">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex-shrink-0 border border-red-600/40 bg-red-600/10 flex items-center justify-center">
                  <MapPin size={18} strokeWidth={1.5} className="text-red-500" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Endereço</p>
                  <p className="text-white font-semibold leading-relaxed">{SITE.address}</p>
                </div>
              </div>
            </div>

            {/* Phone */}
            <div className="bg-zinc-900/40 backdrop-blur-md border border-zinc-800/80 p-6">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex-shrink-0 border border-red-600/40 bg-red-600/10 flex items-center justify-center">
                  <Phone size={18} strokeWidth={1.5} className="text-red-500" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Telefone / WhatsApp</p>
                  <a
                    href={`tel:+${SITE.phoneRaw}`}
                    data-testid="contact-phone-link"
                    className="text-white font-semibold hover:text-red-500 transition-colors"
                  >
                    {SITE.phone}
                  </a>
                </div>
              </div>
            </div>

            {/* Instagram */}
            <div className="bg-zinc-900/40 backdrop-blur-md border border-zinc-800/80 p-6">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex-shrink-0 border border-red-600/40 bg-red-600/10 flex items-center justify-center">
                  <Instagram size={18} strokeWidth={1.5} className="text-red-500" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Instagram</p>
                  <a
                    href={SITE.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-instagram-link"
                    className="text-white font-semibold hover:text-red-500 transition-colors"
                  >
                    {SITE.instagram}
                  </a>
                </div>
              </div>
            </div>

            {/* Hours */}
            <div className="bg-zinc-900/40 backdrop-blur-md border border-zinc-800/80 p-6">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex-shrink-0 border border-red-600/40 bg-red-600/10 flex items-center justify-center">
                  <Clock size={18} strokeWidth={1.5} className="text-red-500" />
                </div>
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Horário de Funcionamento</p>
                  <ul className="space-y-1 text-sm text-zinc-300">
                    <li className="flex justify-between gap-4"><span>Seg — Sex</span><span className="text-white font-semibold">08:00 — 18:00</span></li>
                    <li className="flex justify-between gap-4"><span>Sábado</span><span className="text-zinc-400">Consultar disponibilidade</span></li>
                    <li className="flex justify-between gap-4"><span>Domingo</span><span className="text-zinc-500">Fechado</span></li>
                  </ul>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="location-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-5 text-xs uppercase tracking-widest transition-all duration-300 border border-red-500/60 shadow-[0_0_15px_rgba(229,57,53,0.25)] hover:shadow-[0_0_25px_rgba(229,57,53,0.5)]"
              >
                <MessageCircle size={16} strokeWidth={2} /> WhatsApp
              </a>
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="location-maps-btn"
                className="inline-flex items-center justify-center gap-2 bg-transparent text-white font-bold py-4 px-5 text-xs uppercase tracking-widest transition-all duration-300 border border-zinc-700 hover:border-zinc-400 hover:bg-white/5"
              >
                <Navigation size={16} strokeWidth={2} /> Como chegar
              </a>
            </div>
          </motion.div>

          {/* Right: Map */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-7 relative border border-zinc-800 bg-zinc-900/40 overflow-hidden min-h-[480px]"
          >
            <iframe
              title="Localização Full Force Car Premium"
              src={MAPS_EMBED}
              className="absolute inset-0 w-full h-full"
              style={{ border: 0, filter: "grayscale(0.4) contrast(1.1) invert(0.92) hue-rotate(180deg)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              data-testid="google-maps-iframe"
            />
            <div className="absolute top-4 left-4 bg-black/85 backdrop-blur-md border border-red-600/40 px-4 py-3 pointer-events-none">
              <p className="text-[10px] uppercase tracking-widest text-red-500 font-bold">Full Force Car Premium</p>
              <p className="text-xs text-white mt-0.5">Jundiaí — SP</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
